import { useCallback, useEffect, useState } from "react";

const DEFAULT_VERSION = "2.15.2309.2601";
// Same element id as useSolaIFields (the agreement widget), so the script only ever loads once.
const SCRIPT_ID = "sola-ifields-script";
const TOKEN_TIMEOUT_MS = 30000;
const FRAME_WAIT_MS = 10000;

/**
 * Waits until both card iframes are in the DOM and reachable. Inside a modal they mount a beat
 * after this hook's effect runs, and setAccount does nothing useful before they exist.
 */
const waitForCardFrames = () =>
    new Promise((resolve, reject) => {
        const deadline = Date.now() + FRAME_WAIT_MS;
        const tick = () => {
            const card = document.querySelector('[data-ifields-id="card-number"]');
            const cvv = document.querySelector('[data-ifields-id="cvv"]');
            if (card?.contentWindow && cvv?.contentWindow) {
                resolve();
                return;
            }
            if (Date.now() > deadline) {
                reject(new Error("The card form did not load. Close this window and try again."));
                return;
            }
            setTimeout(tick, 100);
        };
        tick();
    });

/**
 * Sola iFields for taking a card in admin.
 *
 * The card number and CVV are typed into Sola's iframes, so the raw digits never touch this app —
 * getCardTokens only returns SUTs. Expiry is not an iField; the caller collects it. The key is
 * SoftCar's own iFields key: Vendor ID lets its SUTs be charged on any merchant's account.
 */
const useSolaCardFields = ({ enabled = true } = {}) => {
    const xKey = process.env.REACT_APP_SOLA_IFIELDS_XKEY;
    const version = process.env.REACT_APP_SOLA_IFIELDS_VERSION || DEFAULT_VERSION;
    const iframeSrc = `https://cdn.cardknox.com/ifields/${version}/ifield.htm`;

    const [ready, setReady] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!enabled) {
            setReady(false);
            return undefined;
        }
        if (!xKey) {
            setError("Card payments are not configured (REACT_APP_SOLA_IFIELDS_XKEY is missing).");
            return undefined;
        }

        let cancelled = false;

        const initFields = async () => {
            if (cancelled || typeof window.setAccount !== "function") return;
            try {
                await waitForCardFrames();
                if (cancelled) return;
                window.setAccount(xKey, "SoftCar", "1.0");
                window.enableAutoFormatting?.();
                setReady(true);
                setError(null);
            } catch (err) {
                if (cancelled) return;
                setError(typeof err === "string" ? err : err?.message || "Could not start the card form.");
                setReady(false);
            }
        };

        const existing = document.getElementById(SCRIPT_ID);
        if (existing) {
            if (typeof window.setAccount === "function") {
                initFields();
            } else {
                existing.addEventListener("load", initFields);
            }
            return () => {
                cancelled = true;
                existing.removeEventListener("load", initFields);
            };
        }

        const script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.src = `https://cdn.cardknox.com/ifields/${version}/ifields.min.js`;
        script.async = true;
        script.onload = initFields;
        script.onerror = () => {
            if (!cancelled) {
                setError("Could not load the card form. Check your connection and try again.");
                setReady(false);
            }
        };
        document.body.appendChild(script);

        return () => {
            cancelled = true;
        };
    }, [enabled, xKey, version]);

    const getCardTokens = useCallback(() => {
        if (typeof window.getTokens !== "function") {
            return Promise.reject(new Error("The card form is not ready yet."));
        }
        return new Promise((resolve, reject) => {
            window.getTokens(
                () => {
                    // getTokens writes each token into its hidden input rather than passing it back.
                    const read = (id) => document.querySelector(`[data-ifields-id="${id}"]`)?.value || "";
                    const sut = read("card-number-token");
                    if (!sut) {
                        reject(new Error("Enter a valid card number."));
                        return;
                    }
                    resolve({ sut, xCVV: read("cvv-token") });
                },
                () => reject(new Error("The card details could not be verified. Check them and try again.")),
                TOKEN_TIMEOUT_MS,
            );
        });
    }, []);

    return { ready, error, iframeSrc, getCardTokens };
};

export default useSolaCardFields;
